import { Injectable } from '@angular/core';
import { map, Observable, retry } from 'rxjs';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { AccountBalanceService } from '../balance/account-balance.service';
import { BalanceModel } from 'src/app/models/balance-model';

@Injectable({
  providedIn: 'root'
})
export class RewardsService {

  public customer: CustomerModel;
  accountBalances: BalanceModel = null;
  protected loyaltyPointsMap = new Map();
  protected loyaltyPValue = "points";

  constructor(
    public userSessionService: UserSessionService,
    private webservices: WebservicesService,
    public balanceProvider: AccountBalanceService,
  ) {
    this.userSessionService.getCustomer().subscribe(customer => {
      this.customer = customer;
    });

    this.balanceProvider.getBalance().subscribe(
      (response) => {
        this.accountBalances = response;
      }
    );
  }

  public handleLoyaltyPoints(loyaltyPoints: any) {
    if (!loyaltyPoints || loyaltyPoints.result !== 1 || !loyaltyPoints.payload) {
      return null;
    }
    const { payload } = loyaltyPoints;
    const monthlyTiers = payload?.monthly_tier || [];
    const highestTierLevel = Math.max(...monthlyTiers.map(tier => tier.level || 0));
    const currentTier = Number(payload.current_loyalty_level) || 0;
    const rwpPoints = this.accountBalances?.RWP?.availableBalance ?? 0;
  
    const loyaltyInfo = {
      totalRewards: Math.trunc(rwpPoints) || 0,
      pointsToNextLevel: Math.trunc(payload.next_level_points) || 0,
      totalRewardsMonth: Math.trunc(payload.earned_monthly?.value) || 0,
      currentTier: currentTier,
      nextTierName: payload.next_level_name || "",
      currentTierName: payload.current_level_name || "",
      nextLevelRateBetterPercent: Number(payload.next_level_rate_better_percent) || 0,
      currentProgress: Math.trunc(payload.current_progress) || 0,
      nextLevelPoints: Math.trunc(payload.next_level) || 0,
      isLastTier: highestTierLevel <= currentTier
    };
  
    return loyaltyInfo;
  }


  callGetSignupOffers(data): Observable<PlatformResponse<any>> {
    let obj: any = {
      clientapp: data.clientapp,
    }
    return this.webservices.get('rewards/get-signup-offers', obj);
  }

  callGetSignupOffer(promoCode): Observable<PlatformResponse<any>> {
    let obj: any = {
      promo_code: promoCode
    }
    return this.webservices.get('rewards/get-signup-offer', obj);
  }


  callGetBonusOffers() {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken()
    }

    return this.webservices.get('rewards/get-bonus-offers', obj);
  }

  callGetCustomerCasinoBonusesActiveOptIn() {
    let obj: any = {
      customer_id: this.customer.customerId,
      optin: false,
      session_token: this.userSessionService.getSessionToken()
    }

    return this.webservices.get('rewards/get-customer-casino-bonuses-active', obj);
  }

  callRFBBonusOptIn(data) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken()
    }
    if (data && data.only_active) obj.only_active = true;
    return this.webservices.get('bonus/get-customer-rfb-bonuses', obj);
  }

  callBonusOptIn(bonus_id: string) {
    let obj: any = {
      customer_casino_bonus_id: bonus_id,
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken()
    }

    return this.webservices.post('rewards/bonus-opt-in', obj);
  }

  callRFBBonusForfeit(bonus_id: string) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      customer_rfb_bonus_id: bonus_id
    }

    return this.webservices.post('bonus/rfb-bonus-forfeit', obj);
  }

  getInternalFreeSpins() {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
    }

    return this.webservices.get('rewards/get-bonus-free-spins', obj);
  }

  callGetLoyaltyPoints(customerId?) {
    return new Promise((resolve, reject) => {
      if (typeof customerId == 'undefined') {
        customerId = this.customer.customerId
      }
      let obj: any = {
        customer_id: customerId,
        session_token: this.userSessionService.getSessionToken()
      }
      if(!this.loyaltyPointsMap.has(this.loyaltyPValue)) {
        this.webservices.get('rewards/get-loyalty-points', obj)
        .pipe(map(item => this.handleLoyaltyPoints(item)))
        .subscribe((response) => {
          this.loyaltyPointsMap.set(this.loyaltyPValue, response);
          resolve(response);
        }, (error) => {
          reject(error);
        });
      } else {
        resolve(this.loyaltyPointsMap.get(this.loyaltyPValue));
      }
    });
  }

  removeLoyaltyPoints(){
    this.loyaltyPointsMap.delete(this.loyaltyPValue)
  }

  callGetRewardExchangeRate(conversion_to?: string) {
    if (typeof conversion_to == 'undefined') {
      conversion_to = this.accountBalances.getDefaultCurrency();
    }

    let obj: any = {
      conversion_to: conversion_to,
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken()
    }
    return this.webservices.get('rewards/get-reward-exchange-rate', obj);
  }

  callConvertRewardsPoints(amount: number) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      conversion: amount,
      convert_to: this.accountBalances.getDefaultCurrency(),
    }

    return this.webservices.get('rewards/convert-rewards-points', obj);
  }

  getAllLoyaltyPoints() {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken()
    }

    return this.webservices.get('customer/loyalty-points', obj);
  }

  convertExternalPoints(amount, bucket) {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      conversion:amount,
      bucket:bucket
    }

    return this.webservices.get('rewards/convert-external-rewards-points', obj);
  } 
}
